import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { Box, Grid, Hidden, Typography } from "@material-ui/core";
import FormAutoComplete, {
  FormSelect,
} from "../../../forms/formik/FormSelectField";
import FormRadioButton from "../../../forms/formik/FormRadioField";
import FormTextInput from "../../../forms/formik/FormTextInput";
import {
  CartItems,
  InvestorFundDetails,
  Options,
  SwitchInOptions,
} from "../../../../store/types";
import {
  SWITCH_MODE_OPTIONS,
  SWITCH_TYPE_OPTIONS,
  getSwitchFolios as getFolioOptions,
  getSwitchOutOptions,
} from "./";
import CustomButton from "../../../forms/CustomButton";
import SubmitButton from "../../../forms/formik/FormSubmitButton";
import {
  addItemsToCart,
  getAssetClasses,
  getSwitchInSchemesList,
  updateCartItem,
} from "../../../../store/actions";
import InvestorDetailsPreview from "../InvestorDetails";
import { SwitchDetails } from "../../transactions/tranUtils";
import { useAppSelector } from "../../../../store/hooks";
import {
  convertToInr,
  getFundOptions,
  getSelectedCartItem,
} from "../../../../utils/cart";
interface Values {
  amc: string;
  folio: string;
  switchOutScheme: string;
  category: string;
  switchInScheme: string;
  type: string;
  mode: string;
  value: string;
  [x: string]: any;
}
const validationSchema = (fundDetails: InvestorFundDetails[]) =>
  yup.object().shape({
    amc: yup.string().required("AMC is required"),
    folio: yup
      .string()
      .required("Folio is required")
      .test("zbf", "Zero balance folios are not allowed", function (value) {
        const { amc } = this.parent;
        const switchOptions = getSwitchOutOptions(fundDetails, amc, value);
        return Boolean(switchOptions.length);
      }),
    switchOutScheme: yup.string().required("Switch Out Scheme is required"),
    category: yup.string().required("Category is required"),
    switchInScheme: yup.string().required("Switch In Scheme is required"),
    type: yup
      .string()
      .required("Switch Type is required")
      .equals(["partial", "full"]),
    mode: yup
      .string()
      .required("Switch Mode is required")
      .when("type", {
        is: (value: string) => value === "full",
        then: (schema) =>
          schema.equals(
            ["units"],
            "Switch Mode can only be units when Switch Type is Full"
          ),
        otherwise: (schema) => schema,
      }),
    value: yup
      .string()
      .when("type", {
        is: "full",
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required("Switch Value is required"),
      })
      .test("minAmount", "", function (value) {
        const { minAmount, maxAmount, type, mode } = this.parent;

        const balance = String(maxAmount).replaceAll(",", "");

        if (type === "full") return true;

        if (mode === "units" && !!value) return true;

        if (!balance) return true;

        if (parseInt(value) > parseFloat(balance.replaceAll(",", "")))
          return this.createError({
            message: `Available balance is ${convertToInr(balance)}`,
          });

        if (!minAmount) return true;

        if (
          parseFloat(balance) > parseFloat(minAmount) &&
          parseInt(value) < parseFloat(minAmount)
        ) {
          return this.createError({
            message: `Switch-In Minimum amount is ${minAmount}`,
          });
        }

        if (
          parseFloat(minAmount) > parseFloat(balance.replaceAll(",", "")) &&
          parseFloat(value) * (1 + 0.05) < parseFloat(minAmount)
        ) {
          return this.createError({
            message: `You do not have enough amount to switch`,
          });
        }

        return true;
      }),
  });
interface OnChange extends ChangeEvent<{ name?: string; value: any }> {}
export const Switch = (props: any) => {
  const dispatch = useDispatch();
  const location = useLocation<{
    cartItemId: string;
    amc: string;
  }>();
  const history = useHistory();

  const { fundDetails } = useAppSelector((state) => state.cartReducer);

  // @ts-ignore
  const cartItems: CartItems<"SWITCH"> = useAppSelector(
    (state) => state.cartReducer.cartItems
  );
  const { payload } = getSelectedCartItem(cartItems, location.state);

  const [categoryOptions, setCategoryOptions] = useState<Options>([]);
  const [switchOutOptions, setSwitchOutOptions] = useState<
    ReturnType<typeof getSwitchOutOptions>
  >([]);

  const [initialState, setInitialState] = useState<Values>({
    amc: "",
    folio: "",
    switchOutScheme: "",
    category: "",
    switchInScheme: "",
    type: "",
    mode: "",
    value: "",
  });
  const [switchInOptions, setSwitchInOptions] = useState<SwitchInOptions>([]);

  const { state, pathname } = location;

  const isEdit = pathname.includes("/edit-transaction");

  const fundOptions = useMemo(() => getFundOptions(fundDetails), [fundDetails]);

  const onSubmit = async (values: Values) => {
    const selectedCartItem = cartItems.find(
      ({ cartItemId: id }) => id === state.cartItemId
    );
    const { payload, cartId } = selectedCartItem;
    const mobile =
      fundDetails.find(({ Acno, Fund, PAN }) => {
        if (values.amc !== Fund) return false;
        if (values.folio.toString() !== Acno.toString()) return false;
        if (payload.pan !== PAN) return false;
        return true;
      })?.Mobile || "";

    const email =
      fundDetails.find(({ Acno, Fund, PAN }) => {
        if (values.amc !== Fund) return false;
        if (values.folio.toString() !== Acno.toString()) return false;
        if (payload.pan !== PAN) return false;
        return true;
      })?.email || "";

    const { amc, folio, switchInScheme, switchOutScheme, value } = values;

    const switchOutOptions = getSwitchOutOptions(fundDetails, amc, folio);

    const selectedSwitchInScheme = switchInOptions.find(
      ({ value }) => value === switchInScheme
    );
    const selectedSwitchOutScheme = switchOutOptions.find(
      ({ value }: any) => value === switchOutScheme
    );

    const requestBody = {
      ...payload,
      mobile,
      email,
      cartId,
      cartItemId: state.cartItemId,
      folio,
      fund: amc,
      scheme: selectedSwitchOutScheme.scheme,
      plan: selectedSwitchOutScheme.plan,
      option: selectedSwitchOutScheme.option,
      toScheme: selectedSwitchInScheme.fm_scheme,
      toPlan: selectedSwitchInScheme.fm_plan,
      toOption: selectedSwitchInScheme.fm_option,
      amount: values.mode === "units" ? 0 : value,
      units: values.mode === "units" ? value : 0,
      partial: values.type === "partial",
      switchInSchemeName: selectedSwitchInScheme.label,
      category: values.category,
      switchOutSchemeName: selectedSwitchOutScheme.label,
      taxSaverFlag: selectedSwitchOutScheme.details.TaxSaverFlag,
    };
    const response = await dispatch(updateCartItem(requestBody));

    if (!!response) {
      alert("Item has been updated successfully");
      history.replace("/cart/investor-cart", {
        pan: payload.pan,
      });
    }
  };

  const onAddTransaction = async (values: Values) => {
    const { amc, folio, switchInScheme, switchOutScheme, value } = values;
    const mobile =
      fundDetails.find(({ Acno, Fund, PAN }) => {
        if (values.amc !== Fund) return false;
        if (values.folio.toString() !== Acno.toString()) return false;
        if (payload.pan !== PAN) return false;
        return true;
      })?.Mobile || "";

    const email =
      fundDetails.find(({ Acno, Fund, PAN }) => {
        if (values.amc !== Fund) return false;
        if (values.folio.toString() !== Acno.toString()) return false;
        if (payload.pan !== PAN) return false;
        return true;
      })?.email || "";

    const switchOutOptions = getSwitchOutOptions(fundDetails, amc, folio);

    const selectedSwitchInScheme = switchInOptions.find(
      ({ value }) => value === switchInScheme
    );
    const selectedSwitchOutScheme = switchOutOptions.find(
      ({ value }: any) => value === switchOutScheme
    );

    const selectedFund = fundOptions.find((i) => i.value === amc).label;
    const requestBody = {
      ...payload,
      email,
      mobile,
      folio,
      fund: amc,
      scheme: selectedSwitchOutScheme.scheme,
      plan: selectedSwitchOutScheme.plan,
      option: selectedSwitchOutScheme.option,
      toScheme: selectedSwitchInScheme.fm_scheme,
      toPlan: selectedSwitchInScheme.fm_plan,
      toOption: selectedSwitchInScheme.fm_option,
      amount: values.mode === "units" ? 0 : value,
      units: values.mode === "units" ? value : 0,
      partial: values.type === "partial",
      switchInSchemeName: selectedSwitchInScheme.label,
      switchOutSchemeName: selectedSwitchOutScheme.label,
      category: values.category,
      taxSaverFlag: selectedSwitchOutScheme.details.TaxSaverFlag,
      fundName: selectedFund,
    };
    const response = await dispatch(addItemsToCart(requestBody));

    if (!!response) {
      history.replace("/cart/investor-cart", {
        pan: payload.pan,
      });
    }
  };

  const setInitialValues = async () => {
    try {
      const switchOptions = getSwitchOutOptions(
        fundDetails,
        state.amc,
        payload.folio
      );
      setSwitchOutOptions(switchOptions);

      const selectedSwitchOutScheme = switchOptions.find(
        ({ scheme, plan, option }) =>
          [
            scheme === payload.scheme,
            plan === payload.plan,
            option === payload.option,
          ].every(Boolean)
      );
      const requestBody = { Fund: state.amc, Folio: payload.folio };
      const assetClasses = await getAssetClasses(requestBody)(dispatch);
      const { DtData = [] } = assetClasses;

      setCategoryOptions(
        DtData.map((cat: { CatDesc: string; CatValue: string }) => ({
          label: cat.CatDesc,
          value: cat.CatValue,
        }))
      );

      const switchInSchemesRequestBody = {
        folio: payload.folio,
        fundCode: state.amc,
        category: payload.category,
      };
      const response = await getSwitchInSchemesList(switchInSchemesRequestBody)(
        dispatch
      );

      const newSwitchInOptions = response.filter(
        ({ fm_scheme, fm_plan, fm_option }) => {
          const { scheme, plan, option } = selectedSwitchOutScheme;
          return !(
            scheme === fm_scheme &&
            plan === fm_plan &&
            option === fm_option
          );
        }
      );

      setSwitchInOptions(newSwitchInOptions);

      const selectedSwitchInScheme = newSwitchInOptions.find(
        ({ fm_scheme, fm_plan, fm_option }) => {
          return [
            payload.toScheme === fm_scheme,
            payload.toPlan === fm_plan,
            payload.toOption === fm_option,
          ].every(Boolean);
        }
      );

      setInitialState({
        amc: state.amc,
        category: payload.category || "",
        folio: payload.folio.toString(),
        type: payload.partial ? "partial" : "full",
        switchInScheme: selectedSwitchInScheme?.value || "",
        switchOutScheme: selectedSwitchOutScheme.value,
        mode: parseInt(payload.amount) ? "amount" : "units",
        value: parseInt(payload.amount)
          ? payload.amount.toString()
          : payload.units.toString(),
        minAmount: selectedSwitchInScheme.SIPminamt,
        maxAmount: parseFloat(
          selectedSwitchOutScheme.details.CurValue?.replaceAll(",", "")
        ),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const investorPAN = props?.location?.state?.pan || "";

  const cancelTransaction = () =>
    history.replace("/cart/investor-cart", {
      pan: payload.pan || investorPAN,
    });

  useEffect(() => {
    if (pathname.includes("edit-transaction")) {
      try {
        setInitialValues();
      } catch (error) {
        history.replace("/new-transaction");
      }
    }
  }, []);

  return (
    <Grid container>
      <Grid item lg={9} sm={12}>
        <Box
          sx={{
            margin: "40px",
            marginRight: "10px",
            bgcolor: "white",
            padding: "18px 24px 18px 24px",
            boxShadow: "0px 0px 8px 0px rgba(202, 202, 202, 1);",
            borderRadius: "4px",
          }}
        >
          <Formik
            initialValues={initialState}
            validationSchema={validationSchema(fundDetails)}
            onSubmit={isEdit ? onSubmit : onAddTransaction}
            enableReinitialize
            validateOnBlur
            validateOnChange
          >
            {({ values, setFieldValue, handleSubmit, setValues }) => {
              const { amc, folio, type } = values;

              const folioOptions = getFolioOptions(fundDetails, amc);

              const selectedSwitchOutScheme = switchOutOptions.find(
                ({ value }) => value === values.switchOutScheme
              );

              const handleSwitchOutChange = async (event: any) => {
                const selectedSwitchOutScheme = switchOutOptions.find(
                  ({ value }) => value === event.target.value
                );
                setValues({
                  ...values,
                  minAmount: "1",
                  switchOutScheme: event.target.value,
                  maxAmount: parseFloat(
                    selectedSwitchOutScheme.details.CurValue?.replaceAll(
                      ",",
                      ""
                    )
                  ),
                  type: "partial",
                  mode: "units",
                  value: "",
                  category: "",
                });
                try {
                  const requestBody = { Fund: amc, Folio: folio };
                  const assetClasses = await getAssetClasses(requestBody)(
                    dispatch
                  );
                  const { DtData = [] } = assetClasses;

                  setCategoryOptions(
                    DtData.map(
                      (cat: { CatDesc: string; CatValue: string }) => ({
                        label: cat.CatDesc,
                        value: cat.CatValue,
                      })
                    )
                  );
                } catch (error) {
                  alert(error);
                }
              };
              const handleCategoryChange = async (e: OnChange) => {
                setFieldValue("category", e.target.value);
                setValues({
                  ...values,
                  category: e.target.value,
                  switchInScheme: "",
                  mode: "",
                  type: "",
                  value: "",
                  minAmount: "",
                  maxAmount: "",
                });
                const requestBody = {
                  folio: values.folio,
                  fundCode: values.amc,
                  category: e.target.value,
                };
                const response = await getSwitchInSchemesList(requestBody)(
                  dispatch
                );
                setSwitchInOptions(
                  response.filter(({ fm_scheme, fm_plan, fm_option }) => {
                    const { scheme, plan, option } = selectedSwitchOutScheme;
                    return !(
                      fm_scheme === scheme &&
                      plan === fm_plan &&
                      option === fm_option
                    );
                  })
                );
              };
              const handleSwitchModeChange = (switchMode: any) => {
                if (!switchMode) return;
                const selectedSwitchOutScheme = switchOutOptions.find(
                  (opt) => opt.value === values.switchOutScheme
                );

                const selectedSwitchInScheme = switchInOptions.find(
                  (opt) => opt.value === values.switchInScheme
                );

                if (switchMode === "amount") {
                  setValues({
                    ...values,
                    mode: "amount",
                    minAmount: selectedSwitchInScheme.SIPminamt,
                    maxAmount: parseFloat(
                      selectedSwitchOutScheme.details.CurValue?.replace(",", "")
                    ),
                    value: "",
                  });

                  return;
                }
                setValues({
                  ...values,
                  mode: "units",
                  minAmount: "1",
                  maxAmount:
                    selectedSwitchOutScheme.details.BalUnits.toString(),
                  value: "",
                });
              };

              const handleSwitchTypeChange = (switchType: any) => {
                const selectedScheme = switchOutOptions.find(
                  (opt) => opt.value === values.switchOutScheme
                );

                if (switchType === "full") {
                  setValues({
                    ...values,
                    type: "full",
                    mode: "units",
                    value: selectedScheme.details.BalUnits.toString(),
                  });
                  return;
                }
                setValues({
                  ...values,
                  type: "partial",
                  mode: "units",
                  minAmount: "1",
                  maxAmount: selectedScheme.details.BalUnits.toString(),
                  value: "",
                });
              };

              const scheme = switchOutOptions.find(
                (opt) =>
                  opt.value === values.switchOutScheme &&
                  String(opt.details.Acno) === String(values.folio)
              );
              const checkMultiBrokerFlag =
                scheme?.details?.multibrokflg &&
                scheme?.details?.multibrokflg === "Y";

              const handleAmcChange = (e: OnChange) => {
                setValues({
                  amc: e.target.value,
                  amount: "",
                  category: "",
                  folio: "",
                  swpOption: "",
                  switchInScheme: "",
                  switchOutScheme: "",
                  mode: "",
                  type: "",
                  value: "",
                });
              };
              const handleFolioChange = (e: OnChange) => {
                const switchOptions = getSwitchOutOptions(
                  fundDetails,
                  amc,
                  e.target.value
                );
                setSwitchOutOptions(switchOptions);
                setValues({
                  ...values,
                  amount: "",
                  category: "",
                  folio: e.target.value,
                  switchInScheme: "",
                  switchOutScheme: "",
                  mode: "",
                  type: "",
                  value: "",
                });
              };

              return (
                <Box component="form" onSubmit={handleSubmit}>
                  <Typography
                    component="h4"
                    style={{
                      fontWeight: "800",
                      fontSize: "24px",
                      marginBottom: "20px",
                      fontFamily: "OpenSans-SemiBold",
                    }}
                  >
                    Switch
                  </Typography>
                  <FormAutoComplete
                    name="amc"
                    label={"AMC"}
                    disabled={isEdit}
                    options={fundOptions}
                    onChange={handleAmcChange}
                  />
                  <FormSelect
                    name="folio"
                    label="Folio"
                    disabled={!values.amc}
                    options={folioOptions}
                    onChange={handleFolioChange}
                  />
                  <FormAutoComplete
                    name="switchOutScheme"
                    label="Switch Out Scheme"
                    disabled={!values.folio}
                    options={switchOutOptions}
                    onChange={handleSwitchOutChange}
                  />

                  <Box
                    style={{
                      backgroundColor: "rgba(0, 124, 197, 0.1)",
                      display: "flex",
                      justifyContent: "space-evenly",
                      paddingTop: 20,
                      paddingBottom: 20,
                      marginBottom: 20,
                    }}
                  >
                    <Box>
                      {!checkMultiBrokerFlag && (
                        <>
                          <SwitchDetails name={"Balance Units"} />
                          <SwitchDetails name={"Current Value"} />
                        </>
                      )}
                      <SwitchDetails name={"NAV as on Date"} />
                    </Box>

                    <Box>
                      {!checkMultiBrokerFlag && (
                        <>
                          <SwitchDetails
                            name={scheme?.details?.BalUnits || ""}
                          />
                          <SwitchDetails
                            name={scheme?.details?.CurValue || ""}
                          />
                        </>
                      )}
                      <SwitchDetails name={scheme?.details?.LastNAV || ""} />
                    </Box>
                  </Box>

                  <FormAutoComplete
                    name="category"
                    label="Category"
                    disabled={!values.switchOutScheme}
                    options={categoryOptions}
                    onChange={handleCategoryChange}
                  />
                  <FormAutoComplete
                    name="switchInScheme"
                    label="Switch In Scheme"
                    disabled={!values.category}
                    options={switchInOptions}
                  />
                  <FormRadioButton
                    name="type"
                    label="Switch Type"
                    options={SWITCH_TYPE_OPTIONS}
                    disabled={!values.switchInScheme}
                    onChange={handleSwitchTypeChange}
                  />
                  {values.type !== "full" ? (
                    <FormRadioButton
                      name="mode"
                      label="Switch Mode"
                      options={SWITCH_MODE_OPTIONS}
                      disabled={!values.type || type === "full"}
                      onChange={handleSwitchModeChange}
                    />
                  ) : null}

                  <FormTextInput
                    name="value"
                    label="Switch Value"
                    type="number"
                    disabled={!values.mode || type === "full"}
                  />
                  <Box display="flex">
                    <CustomButton
                      label="Go To Cart"
                      variant="outlined"
                      onClick={cancelTransaction}
                    />
                    <SubmitButton
                      label={isEdit ? "Update" : "Add To Cart"}
                      variant="contained"
                    />
                  </Box>
                </Box>
              );
            }}
          </Formik>
        </Box>
      </Grid>
      <Hidden mdDown>
        <Grid item lg={3}>
          <InvestorDetailsPreview
            investorData={[
              { label: "Investor Name", value: payload.investorName },
              { label: "PAN", value: payload.pan },
              { label: "Email", value: payload.email },
              { label: "Mobile", value: payload.mobile },
              {
                label: "EUIN Declaration",
                value: payload.euinDeclaration === "Y" ? "No" : "Yes",
              },
              { label: "EUIN", value: payload.euin || "-" },
              { label: "Sub ARN Code", value: payload.subArn || "-" },
              { label: "Sub Broker Code", value: payload.subBroker || "-" },
            ]}
          />
        </Grid>
      </Hidden>
    </Grid>
  );
};
